import React, { useCallback, useMemo, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { useQuery } from "../Hooks";
import * as rmaRequests from "../../requests/RMAManagementRequests";
import { PageWrapper } from "../UI/Grid";
import Table from "../UI/Table";
import _ from "lodash";
import { Body } from "../UI/Text";
import Button from "../UI/Button";
import { Flex } from "reflexbox";
import moment from "moment";
import { createRMADetail } from "../../requests/rmaDetailManagementReqs";
import UserContext from "../../context/user/UserContext";

const { getRMAs, deleteRMA, createRMA } = rmaRequests;

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const RMAsPage = () => {
  const navigate = useNavigate();
  const { pid } = useParams();
  const theme = useTheme();
  const { activeUser } = useContext(UserContext);
  const { roleLevel = 0, groupId = undefined } = activeUser || {};

  const result = useQuery({
    queryID: "RMAs",
    request: getRMAs,
    queryVariables: { pid, groupId },
    skip: !pid,
  });

  const { data, isLoading, refetchQuery, setData } = result || {};

  const { rows: RMAs = [], count } = data || {};

  const onCardClick = useCallback(
    async (id) => {
      if (id === "create") {
        const result = await createRMA({
          input: { pid, rmaCreated: "0", groupId },
        });
        if (result?.id) {
          await createRMADetail({
            input: {
              RMAId: result.id,
              pid,
              productNumber: "",
              quantity: 1,
              orderedQuantity: 0,
            },
          });

          return navigate(`/RMA/${result.id}`);
        }
      } else {
        navigate(`/RMA/${id}`);
      }
    },
    [navigate, pid, groupId]
  );

  const onDelete = useCallback(
    async (id) => {
      const newRMAs = [...RMAs];
      _.remove(newRMAs, ({ id: rmaID }) => rmaID === id);
      const newCount = count - 1;
      setData({ rows: newRMAs, count: newCount });

      await deleteRMA({ id });
    },
    [RMAs, count, setData]
  );

  const columns = useMemo(
    () => [
      {
        id: "createdAt",
        data: ({ createdAt }) => (
          <Body center ellipsis>
            {createdAt ? moment(createdAt).format("MM/DD/YYYY") : "—"}
          </Body>
        ),
        header: "Created",
        size: { md: 2, sm: 2, xs: 4 },
      },

      {
        id: "erpOrderNum",
        data: ({ erpOrderNum }) => (
          <Body center ellipsis>
            {erpOrderNum || "—"}
          </Body>
        ),
        header: "Order #",
        size: { md: 2, sm: 2, xs: 4 },
      },
      {
        id: "rmaNumber",
        data: ({ rmaNumber }) => (
          <Body center ellipsis>
            {rmaNumber || "—"}
          </Body>
        ),
        header: "RMA #",
        size: { md: 2, sm: 2, xs: 4 },
      },

      {
        id: "status",
        data: ({ status, rmaCreated }) => (
          <Body center>
            {!status && !rmaCreated
              ? "Not Submitted"
              : !status
              ? "Submitted"
              : status}
          </Body>
        ),
        header: "Status",
        size: { md: 3, sm: 3, xs: 4 },
      },

      {
        id: "rmaCreated",
        data: "rmaCreated",
        header: "Created",
        size: { md: -1, sm: -1, xs: -1 },
      },

      {
        id: "modelNo",
        data: ({ modelNo }) => (
          <Body center ellipsis>
            {modelNo || "—"}
          </Body>
        ),
        header: "Model #",
        size: { md: -1, sm: -1, xs: -1 },
      },
      {
        id: "actions",
        display: (props) => {
          let RMA = props.cell.row.original;

          const { id, rmaCreated } = RMA || {};

          return (
            <ButtonsWrapper id="actions">
              <Button
                size="small"
                color={theme.secondary}
                reverse
                iconName={rmaCreated ? "faEye" : "faPencil"}
                onClick={(e) => {
                  e.stopPropagation();
                  onCardClick(id);
                }}
              />
              {roleLevel === 9 && (
                <Button
                  size="small"
                  color={theme.danger}
                  reverse
                  iconName="faTrashCan"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(id);
                  }}
                />
              )}
            </ButtonsWrapper>
          );
        },
        header: "Actions",
        sort: false,
        size: { lg: 3, md: 4, sm: 0, xs: 0 },
      },
    ],
    [theme.secondary, theme.danger, roleLevel, onCardClick, onDelete]
  );

  return (
    <>
      <PageWrapper>
        <Table
          createLink={() => onCardClick("create")}
          createButtonText="Create RMA"
          createButtonIcon="faPersonWalkingArrowLoopLeft"
          hideToggle
          title="RMA Requests"
          subtitle="Listed here are Return Merchandise Authorization (RMA) requests that have been submitted by users of your current group."
          hideRefetch={false}
          refetchData={refetchQuery}
          columns={columns}
          backButtonLink="/Administration"
          data={RMAs}
          totalCount={count}
          isLoading={isLoading}
          onRowClick={(row) => onCardClick(row?.original?.id)}
        />
      </PageWrapper>
    </>
  );
};

export default RMAsPage;
